import * as Sentry from '@sentry/react';
import { addNonEnumerableProperty } from '@sentry/utils';
import { AnyAction, Middleware } from 'redux';
import type { SentryEnhancerOptions } from '@sentry/react/build/types/redux';

const ACTION_BREADCRUMB_CATEGORY = 'redux.action';
const ACTION_BREADCRUMB_TYPE = 'info';

const defaultOptions: SentryEnhancerOptions = {
	attachReduxState: true,
	actionTransformer: action => action,
	stateTransformer: (state: unknown) => state || null
};

/**
 * This middleware adds redux state to reported errors.
 *
 * This implementation is based on the official Sentry implementation for the redux enhancer found [here]{@link https://github.com/getsentry/sentry-javascript/blob/2c3f09c40fc35aaa2998a7f14a802ab0f0560227/packages/react/src/redux.ts#L90}
 *
 * The benefit of using it as a middleware instead of an enhancer is that it gives support for dynamic reducers
 *
 * @param enhancerOptions - Options for the middleware
 * @returns Redux middleware
 */
export const errorReporterReduxMiddleware = (
	enhancerOptions: Partial<SentryEnhancerOptions> = {}
): Middleware<Record<string, never>, AnyAction> => {
	const options = {
		...defaultOptions,
		...enhancerOptions
	};

	const globalScope = Sentry.getGlobalScope();

	if (options.attachReduxState) {
		globalScope.addEventProcessor((event, hint) => {
			try {
				if (event.type === undefined && event?.contexts?.state?.state.type === 'redux') {
					// eslint-disable-next-line no-param-reassign
					hint.attachments = [
						...(hint.attachments || []),
						{ filename: 'redux_state.json', data: JSON.stringify(event?.contexts?.state?.state?.value) }
					];
				}
			} catch (_) {
				// empty
			}
			return event;
		});
	}

	return store => next => (action: AnyAction) => {
		const result = next(action);
		const newState = store.getState();

		const scope = Sentry.getCurrentScope();

		/* Action breadcrumbs */
		const transformedAction = options.actionTransformer ? options.actionTransformer(action) : undefined;
		if (typeof transformedAction !== 'undefined' && transformedAction !== null) {
			Sentry.addBreadcrumb({
				category: ACTION_BREADCRUMB_CATEGORY,
				data: transformedAction,
				type: ACTION_BREADCRUMB_TYPE
			});
		}

		/* Set latest state to scope */
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		const transformedState = options.stateTransformer ? options.stateTransformer(newState) : undefined;
		if (typeof transformedState !== 'undefined' && transformedState !== null) {
			const client = Sentry.getClient();
			const clientOptions = client && client.getOptions();
			const normalizationDepth = (clientOptions && clientOptions.normalizeDepth) || 3;

			// Set the normalization depth of the redux state to the configured `normalizeDepth` option or a sane number as a fallback
			const newStateContext = { state: { type: 'redux', value: transformedState as unknown } };
			addNonEnumerableProperty(
				newStateContext,
				'__sentry_override_normalization_depth__',
				3 + normalizationDepth // 3 layers for `state.value.transformedState` + actual state
			);

			scope.setContext('state', newStateContext);
		} else {
			scope.setContext('state', null);
		}

		/* Allow user to configure scope with latest state */
		const { configureScopeWithState } = options;
		if (typeof configureScopeWithState === 'function') {
			configureScopeWithState(scope, newState);
		}

		return result;
	};
};
